import { createAsyncThunk } from '@reduxjs/toolkit';

import { CareerPathScoreSection } from '@backrs_ui/common-ui';

import { CareerPathWithScore, CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { withSettledStartTerm } from '@backrs_ui/grow-ui/src/pages/Survey/survey.util';
import { OwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

export type CareerPathResult = Readonly<{
  career_path_id: number;
  sum: number;
  ranking: CareerPathScoreSection;
}>;

export function toCareerPathResults(paths: CareerPathWithScore[]): CareerPathResult[] {
  return paths.map((cp) => ({
    career_path_id: cp.careerPath.id,
    sum: cp.sum,
    ranking: cp.scoreSection,
  }));
}

type UpdateResponseSetInput = Readonly<{
  responseSetId: string;
  userResponseSet: CareerUserResponseSet;
  tier: string;
  entrepreneurshipRecommended: boolean;
  careerPathResults: CareerPathResult[];
  ownershipLevel?: OwnershipLevel | null;
}>;

export const updateResponseSetOnAPI = createAsyncThunk(
  'userResponse/updateResponseSetOnAPI',
  async (
    {
      responseSetId,
      userResponseSet,
      tier,
      entrepreneurshipRecommended,
      careerPathResults,
      ownershipLevel,
    }: UpdateResponseSetInput,
    { rejectWithValue },
  ) => {
    try {
      const response = await fetch(`${ENVIRONMENT_URL}/career-user-response-set/${responseSetId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          user_response_set: withSettledStartTerm(userResponseSet),
          tier,
          entrepreneurship_recommended: entrepreneurshipRecommended,
          career_path_results: careerPathResults,
          ownership_level: ownershipLevel,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');

        throw new Error(
          `Failed to update response set: ${response.status} ${response.statusText}\n${errorBody}`,
        );
      }

      const updatedResponseSet = (await response.json()) as CareerUserResponseSet;

      return { userResponseSet: updatedResponseSet };
    } catch (error) {
      return rejectWithValue(
        `Failed to update response set: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  },
);
